/**
 * elvish文档：https://base.xiaojukeji.com/docs/i18n-hub/7293
 */
import { numberToCurrency, currencyToNumberString } from '@didi/elvish-lib-javascript';
import qs from 'qs';
import dayjs from 'dayjs';
import { i18nTranslate } from '@didi/i18n-plugin';

import { DnChannel, IfValid, SettlementBillState } from '@/config';

export * from './math';
export * from './responsive';
// 获取公参参数
export const getParams = () => {
  // const storedData: any = localStorage.getItem('wujieData');
  // const parsedData = JSON.parse(storedData || '{}');
  // const urlParams = getAllQuery();
  const params = {
    lang: 'en-US' // 'pt-BR', 'en-US', 'zh-CN'
    // ...parsedData,
    // ...urlParams
  };

  return params;
};

// 获取货币单位
export const getCurrency = () => {
  return numberToCurrency(0, true)?.replace(/\{.*?\}/g, '');
};

// 去掉elvish的货币单位后的值，如'-R$5,01' -> '-5,01'; needSign为true时，保留正负号，如'R$5,01' -> '+5,01'
export const getAmountNumber = (text: string, needSign = false) => {
  if (!text) {
    return '';
  }
  if (needSign) {
    return text.includes('-') ? currencyToNumberString(text) : `+${currencyToNumberString(text)}`;
  }
  return currencyToNumberString(text);
};

/**
 * 将 "-R$1.100,00" 或者 "-1.100,00" 转换成可计算的number值 -1100.00
 * 举例：elvish: textAll = ["-R$", "1.100,00", "0", "-R$1.100,00"], 传入：text = textAll[3]
 * 得到 amountNum = -1100.00
 */
export const getAmountTrueNum = (text: string) => {
  const amountString = getAmountNumber(text);
  const amountNum = parseFloat(amountString.replace('.', '').replace(',', '.')) || 0;

  return amountNum;
};

// 拼接 URL
export const urlQuery = (url: string, query: { [key: string]: any }) =>
  `${url}?${qs.stringify(query, { encode: false })}`;

// 获取 URL 上参数
export const getAllQuery = () => {
  const query: any = {
    ...qs.parse(window.location.hash.split('?')[1], { decoder: str => decodeURIComponent(str) }),
    ...qs.parse(window.location.search.split('?')[1], { decoder: str => decodeURIComponent(str) })
  };

  return query;
};

// 打开新页面
export const openNewPage = ({ key, params }: { key: string; params: any }) => {
  const url = urlQuery(`#/${key}`, params);

  window.open(url, '_blank');
};

// 内部跳转
export const inJumpTo = (data: any) => {
  const { key, params, callback, replace = false } = data;
  const url = urlQuery(`/${key}`, params);

  callback(url, { replace });
};

// 去掉字符串的前后空格
export const toTrim = (value: string) => {
  return value?.trim();
};

// 下载Excel文件
export const downloadExcel = (res: any) => {
  const { data, headers } = res;
  const blob = new Blob([data], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
  });
  // 兼容不同浏览器的URL对象 & 创建下载链接
  const url = (window.URL || window.webkitURL).createObjectURL(blob);
  const link = document.createElement('a');

  link.href = url;
  let resName;
  const disposition = headers['content-disposition'];

  if (disposition.indexOf('UTF-8') !== -1) {
    // eslint-disable-next-line
    let name = disposition?.split?.("''")?.[1];
    resName = decodeURIComponent(name);
  } else {
    const name = disposition?.split?.('=')?.[1] || ' data.xlsx ';

    resName = name.substring(1, name.length - 1);
  }
  link.download = resName;
  // 触发点击事件执行下载
  link.click();
};

// blob处理
const getBlob = (url: string, cb: any) => {
  const xhr = new XMLHttpRequest();

  xhr.open('GET', url, true);
  xhr.responseType = 'blob';
  xhr.onload = () => {
    if (xhr.status === 200) {
      cb(xhr.response);
    }
  };
  xhr.send();
};

// 文件路径处理
const saveAs = (blob: any, filename: string) => {
  const link = document.createElement('a');
  const body = document.querySelector('body');

  link.href = window.URL.createObjectURL(blob);
  link.download = filename;
  // fix Firefox
  link.style.display = 'none';
  body!.appendChild(link);
  link.click();
  body!.removeChild(link);
  window.URL.revokeObjectURL(link.href);
};

// 下载文件( url：下载路径，name：文件保存的名称)
export const handleDownload = (file: any) => {
  getBlob(file.url, (blob: any) => {
    saveAs(blob, file.name);
  });
};

export const rangePickerPresets: any = () => {
  return [
    {
      label: i18nTranslate('GDriver_B_KVkL_ZHjF'),
      value: [dayjs().subtract(6, 'day').startOf('day'), dayjs()]
    },
    {
      label: i18nTranslate('GDriver_B_CkMV_hWxl'),
      value: [
        dayjs()
          .subtract(dayjs().day() ? dayjs().day() - 1 : 6, 'day')
          .startOf('day'),
        dayjs()
      ]
    },
    {
      label: i18nTranslate('GDriver_B_tuUz_ZGks'),
      value: [
        dayjs()
          .subtract(dayjs().day() ? dayjs().day() + 6 : 13, 'day')
          .startOf('day'),
        dayjs().subtract(dayjs().day() ? dayjs().day() : 7, 'day')
      ]
    },
    {
      label: i18nTranslate('GDriver_B_plYK_TBWO'),
      value: [dayjs().startOf('month').startOf('day'), dayjs()]
    }
  ];
};

// 获取商品展示状态集合
export const getDisplayStatus = () => {
  return [
    // 1: 已上架，2:已下架
    {
      label: i18nTranslate('Internal_Tools_B_OZGf_eIJG'),
      value: 1
    },
    {
      label: i18nTranslate('Internal_Tools_B_hORS_vRXF'),
      value: 2
    }
  ];
};

// 获取D/N发行方
export const getDnChannel = () => {
  return [
    // 1: 平台; 2:油站
    {
      label: i18nTranslate('Internal_Tools_B_FcHg_Nkhf', { brand_name: 99 }),
      value: DnChannel.Platform
    },
    {
      label: i18nTranslate('Internal_Tools_B_NVMK_FtoH'),
      value: DnChannel.GasStation
    }
  ];
};

// 获取D/N状态
export const getDnStatus = () => {
  return [
    // 0:无效; 1: 有效
    {
      label: i18nTranslate('Internal_Tools_B_IRRc_hZIm'),
      value: IfValid.Invalid
    },
    {
      label: i18nTranslate('Internal_Tools_B_Ghxe_pCWK'),
      value: IfValid.Valid
    }
  ];
};

/**
 * omega上报账单状态：
 * Pending // 待确认
 * Auditing  // 99审核中
 * Rejected  // 99驳回
 * Paid  // 99已支付
 */
export const omegaSettlementBillState = {
  [SettlementBillState.Pending]: 'Pending',
  [SettlementBillState.Auditing]: 'Auditing',
  [SettlementBillState.Rejected]: 'Rejected',
  [SettlementBillState.Paid]: 'Paid'
};
